/* eslint-disable max-lines */
import { SourcesType } from '@recruitee/candidate-types';
import { EEODataStatus } from '@recruitee/eeo-types';
import { TGdprStatus } from '@recruitee/gdpr-types';
import { TOfferKind, TOfferStatus } from '@recruitee/offers-types';
import { RequisitionStatus } from '@recruitee/requisitions-types';
import { Visibility } from '@recruitee/visibility-types';

type Any = {
  has_any?: boolean;
  has_none?: boolean;
};

type Followed = {
  has_any_followed?: boolean;
  has_none_followed?: boolean;
};

export enum DisqualifyKind {
  System = 'system',
  Admin = 'admin',
}

export type CandidateSelectOption = QuickSearchCandidateRecord;

export type QuickSearchCandidateRecord = {
  id: number;
  name: string;
  photoThumbUrl: string;
  phones: string[];
  emails: string[];
  viewed: boolean;
  importance: number;
  offers: Array<{
    id: number;
    kind: 'job' | 'talent_pool';
    title: string;
  }>;
  highlight: { [key: string]: string[] };
  isHired: boolean;
  initials: string;
  hasAvatar: boolean;
  isAnonymous?: boolean;
};
export type QuickSearchOfferRecord = {
  id: number;
  slug: string;
  kind: 'job' | 'talent_pool';
  title: string;
  department: string;
  importance: number;
  highlight: { [key: string]: string[] };
};
export type QuickSearchRequisitionRecord = {
  id: number;
  guid: string;
  companyId: number;
  followingAdminsIds: number[];
  title: string;
  highlight: { [key: string]: string[] };
  score: number;
  status: RequisitionStatus;
  archivedAt: number;
};
export type QuickSearchSearchSegmentRecord = {
  id: number;
  name: string;
  highlight: { [key: string]: string[] };
};
export type QuickSearchResponse = {
  candidates: {
    total: number;
    hits: QuickSearchCandidateRecord[];
  };
  jobs: {
    total: number;
    hits: QuickSearchOfferRecord[];
  };
  talentPools: {
    total: number;
    hits: QuickSearchOfferRecord[];
  };
  searchSegments: {
    total: number;
    hits: QuickSearchSearchSegmentRecord[];
  };
  requisitions: {
    total: number;
    hits: QuickSearchRequisitionRecord[];
  };
};

export type InterviewEventSearchResponse = {
  candidates: QuickSearchCandidateRecord[];
};

export type AggregationValue = Array<{ key: string | number; count: number; label?: string }>;

export type FullSearchAggregationsV2 = {
  status?: AggregationValue;
  gdpr_status?: AggregationValue;
  'hiring.status.in'?: AggregationValue;
  source?: AggregationValue;

  cover_letter?: AggregationValue;
  cv?: AggregationValue;
  has_email?: AggregationValue;

  has_email_received_from?: AggregationValue;
  hasnt_email_received_from?: AggregationValue;
  has_email_sent_to?: AggregationValue;
  hasnt_email_sent_to?: AggregationValue;

  'admin.id.in'?: AggregationValue;
  'admin.id.not_in'?: AggregationValue;

  'followers.id.in'?: AggregationValue;
  'followers.id.not_in'?: AggregationValue;

  'jobs.id.in'?: AggregationValue;
  'jobs.id.not_in'?: AggregationValue;
  has_all_jobs?: AggregationValue;

  'talent_pools.id.in'?: AggregationValue;
  'talent_pools.id.not_in'?: AggregationValue;
  has_all_talent_pools?: AggregationValue;

  has_disqualify_reasons?: AggregationValue;
  hasnt_disqualify_reasons?: AggregationValue;

  'stages.name.in'?: AggregationValue;
  'stages.name.not_in'?: AggregationValue;

  'tags.id.in'?: AggregationValue;
  'tags.id.not_in'?: AggregationValue;
  'tags.id.all_in'?: AggregationValue;

  'sources.id.in'?: AggregationValue;
  'sources.id.not_in'?: AggregationValue;
  'sources.id.all_in'?: AggregationValue;

  'open_questions.question_hash.in'?: AggregationValue;
  'open_questions.question_hash.not_in'?: AggregationValue;

  'questionnaire_answers.question_hash.in'?: AggregationValue;
  'questionnaire_answers.question_hash.not_in'?: AggregationValue;

  rating?: AggregationValue;
  positive_ratings?: AggregationValue;

  'tasks.exists'?: AggregationValue;

  'job_department_id.in'?: AggregationValue;
  'job_department_id.not_in'?: AggregationValue;

  'jobs.country_code.in'?: AggregationValue;
  'jobs.country_code.not_in'?: AggregationValue;

  'jobs.state_code.in'?: AggregationValue;
  'jobs.state_code.not_in'?: AggregationValue;

  'jobs.city.in'?: AggregationValue;
  'jobs.city.not_in'?: AggregationValue;

  'jobs.status.in'?: AggregationValue;

  'candidate_fields.search_key.in'?: AggregationValue;
  'candidate_fields.search_key.not_in'?: AggregationValue;
};

export type FullSearchQueryFilterParam = {
  field: 'all';
  query: string;
};
export type FullSearchBooleanFilterParam = {
  field: 'has_cover_letter' | 'has_cv' | 'has_email' | 'has_tasks';
  eq: boolean;
};
export type FullSearchTermFilterParam = {
  field: 'status' | 'source' | 'admin_id' | 'gender' | 'gdpr_status';
  in?: Array<string | number>;
  not_in?: Array<string | number>;
} & Any;
export type FullSearchGdprStatusFilterParam = {
  filter: 'gdpr_status';
  in?: string[];
};
export type FullSearchGdprConsentEverGivenFilterParam = {
  filter: 'gdpr_consent_ever_given';
  eq: boolean;
};
export type FullSearchEEOStatusFilterParam = {
  filter: 'eeo_data_status';
  in?: string[];
};
export type FullSearchDisqualifyKindFilterParam = {
  field: 'disqualify_kind';
  in?: DisqualifyKind[];
};
export type FullSearchDisqualifyReasonsFilterParam = {
  filter: 'disqualifies';
  reason?: { in?: string[]; not_in: string[] } & Any;
};
export type FullSearchTermsFilterParam = {
  field:
    | 'jobs_ids'
    | 'talent_pools_ids'
    | 'assigned_admins_ids'
    | 'interviewers_ids'
    | 'sources'
    | 'disqualify_reasons'
    | 'stages'
    | 'emails_received_from_admins_ids'
    | 'emails_sent_to_admins_ids'
    | 'open_questions_hashes'
    | 'open_questions_answers_hashes'
    | 'questionnaires_answers_hashes'
    | 'questionnaires_answers_answer_hashes'
    | 'school_names'
    | 'degrees'
    | 'majors'
    | 'company_names'
    | 'job_titles'
    | 'job_departments_ids'
    | 'questionnaires_ids';
  has_one_of?: Array<string | number>;
  has_all_of?: Array<string | number>;
  has_none_of?: Array<string | number>;
  id?: {
    in?: number[];
    not_in?: number[];
  };
} & Any &
  Followed;
export type FullSearchRangeFilterParam = {
  field:
    | 'rating'
    | 'positive_ratings'
    | 'created_at'
    | 'events'
    | 'gdpr_uncompleted_change_request_created_at'
    | 'gdpr_uncompleted_removal_request_created_at'
    | 'gdpr_status'
    | 'gdpr_expires_at'
    | 'job_fit_score';
  has_hidden?: boolean;
  gte?: string | number;
  gt?: string | number;
  lte?: string | number;
  lt?: string | number;
  has_one_of?: Array<string | number>;
  has_none_of?: Array<string | number>;
} & Any;
export type FullSearchEducationFilterParam = {
  filter: 'education';
  school?: { contains?: string[]; not_contains?: string[] };
  degree?: { contains?: string[]; not_contains?: string[] };
  major?: { contains?: string[]; not_contains?: string[] };
} & Any;
export type FullSearchStagesFilterParam = {
  filter: 'stages';
  name?: {
    in?: string[];
    not_in?: string[];
    has_any?: boolean;
    has_none?: boolean;
  };
};
export type FullSearchExperienceFilterParam = {
  filter: 'experience';
  company?: { contains?: string[]; not_contains?: string[] };
  title?: { contains?: string[]; not_contains?: string[] };
} & Any;
export type FullSearchEventsFilterParam = {
  filter: 'events';
  kind?: { in?: string[]; not_in?: string[] };
  starts_at?: {
    gte?: string | number;
    gt?: string | number;
    lte?: string | number;
    lt?: string | number;
  };
} & Any;
export type FullSearchTasksFilterParam = {
  filter: 'tasks';
} & Any;
export type FullSearchDuplicateFilterParam = {
  filter: 'potential_duplicate';
  eq: boolean;
};
export type FullSearchDeletedFilterParam = {
  filter: 'deleted';
  eq: boolean;
};

export type FullSearchJobFilterParam = {
  filter: 'jobs';
  department_id?: {
    in?: string[] | number[];
    not_in?: string[] | number[];
  } & Any &
    Followed;
  status?: { in?: string[]; not_in?: string[] };
  country_code?: { in?: string[]; not_in?: string[] };
  state_code?: { in?: string[]; not_in?: string[] };
  city?: { in?: string[]; not_in?: string[] };
  'locations.id'?: { in?: string[] | number[]; not_in?: string[] | number[] };
  id?: {
    in?: string[] | number[];
    not_in?: string[] | number[];
    all_in?: number[];
  } & Any &
    Followed;
};
export type FullSearchTalentPoolFilterParam = {
  filter: 'talent_pools';
  id?: {
    in?: string[] | number[];
    not_in?: string[] | number[];
    all_in?: number[];
  } & Any &
    Followed;
};
export type FullSearchQuestionnaireFilterParam = {
  filter: 'questionnaires_filled' | 'questionnaires_received';
  template_name?: { in?: string[]; not_in?: string[] } & Any;
};
export type FullSearchGenderValueParam = {
  filter: 'gender';
  value?: {
    in?: string[];
    not_in?: string[];
  };
};
export type FullSearchLanguageFilterParam = {
  filter: 'language_skill';
  language_code?: { in?: string[]; not_in?: string[] } & Any;
};
export type FullSearchNationalityFilterParam = {
  filter: 'nationality';
  country_code?: { in?: string[]; not_in?: string[] } & Any;
};
export type FullSearchSkillFilterParam = {
  filter: 'skills';
  text?: { in?: string[]; not_in?: string[] } & Any;
};
export type FullSearchAddressFilterParam = {
  filter: 'address';
  text?: { contains?: string[]; not_contains?: string[] };
} & Any;
export type FullSearchSourcesFilterParam = {
  filter: 'sources';
  id?: {
    all_in?: number[];
    in?: number[];
    not_in?: number[];
  } & Any;
};
export type FullSearchFieldFilterParam = {
  filter: string;
  text?: { contains?: string[]; not_contains?: string[] };
  value?: { in?: string[]; not_in?: string[] };
  number?: {
    gte?: string | number;
    lte?: string | number;
    not_gte?: string | number;
    not_lte?: string | number;
  };
  flag?: { eq?: boolean; not_eq?: boolean };
  date?: {
    gte?: string | number;
    lte?: string | number;
    not_gte?: string | number;
    not_lte?: string | number;
  };
  amount?: {
    gte?: string | number;
    lte?: string | number;
    not_gte?: string | number;
    not_lte?: string | number;
  };
  currency?: string;
} & Any;
export type FullSearchTagsFilterParam = {
  filter: 'tags';
  id?: {
    all_in?: number[];
    in?: number[];
    not_in?: number[];
  } & Any;
};
export type FullSearchHiresFilterParam = {
  filter: 'hiring';
  status?: {
    in?: string[];
  };
  hired_at?: {
    gte?: string | number;
    lte?: string | number;
  };
  job_starts_at?: {
    gte?: string | number;
    lte?: string | number;
  };
};
export type FullSearchDateOfBirthFilterParam = {
  filter: 'date_of_birth';
  date: {
    gte?: string | number;
    lte?: string | number;
  };
};
export type FullSearchFollowersFilterParam = {
  filter: 'followers';
  id?: {
    in?: number[];
    not_in?: number[];
  } & Any;
};
export type FullSearchAdminFilterParam = {
  filter: 'admin';
  id?: {
    in?: number[];
    not_in?: number[];
  } & Any;
};
export type FullSearchWorkLocationsFilterParam = {
  filter: 'locations';
  id?: {
    in?: number[];
    not_in?: number[];
  } & Any;
};

export type FullSearchFilterParam =
  | FullSearchQueryFilterParam
  | FullSearchBooleanFilterParam
  | FullSearchTermFilterParam
  | FullSearchTermsFilterParam
  | FullSearchRangeFilterParam
  | FullSearchGenderValueParam
  | FullSearchAddressFilterParam
  | FullSearchEducationFilterParam
  | FullSearchExperienceFilterParam
  | FullSearchJobFilterParam
  | FullSearchTalentPoolFilterParam
  | FullSearchEventsFilterParam
  | FullSearchTasksFilterParam
  | FullSearchDuplicateFilterParam
  | FullSearchDeletedFilterParam
  | FullSearchQuestionnaireFilterParam
  | FullSearchLanguageFilterParam
  | FullSearchNationalityFilterParam
  | FullSearchSkillFilterParam
  | FullSearchFieldFilterParam
  | FullSearchGdprStatusFilterParam
  | FullSearchGdprConsentEverGivenFilterParam
  | FullSearchEEOStatusFilterParam
  | FullSearchDisqualifyKindFilterParam
  | FullSearchSourcesFilterParam
  | FullSearchTagsFilterParam
  | FullSearchHiresFilterParam
  | FullSearchFollowersFilterParam
  | FullSearchAdminFilterParam
  | FullSearchWorkLocationsFilterParam
  | FullSearchDateOfBirthFilterParam;

export type FullSearchParams = {
  filters?: FullSearchFilterParam[];
  sort_by?: string;
  page?: number;
  limit?: number;
  hits?: boolean;
  aggs?: string[];
  highlight?: boolean;
};

export interface FullSearchCountParams {
  view: 'candidates' | 'notes';
  filters?: FullSearchFilterParam[];
}

export type FullSearchCandidateRecord = {
  id: number;
  companyId: number;
  createdAt: string;
  updatedAt: string;
  lastActivityAt?: string;
  deletedAt?: string;
  softDeletedAt?: string;
  gdprExpiresAt: string;
  gdprStatus: TGdprStatus;
  initials: string;
  gdprUncompletedChangeRequestCreatedAt: string;
  gdprUncompletedRemovalRequestCreatedAt: string;
  name: string;
  photoThumbUrl: string;
  new: boolean;
  duplicated: boolean;
  rating: number;
  positiveRatings: number;
  ratingVisible: boolean;
  emails: string[];
  phones: string[];
  tags: Array<{ id: number; name: string }>;
  sources: Array<{ id: number; name: string }>;
  adminId: number | null;
  deletedBy?: number;
  deletedByName?: string;
  unreadNotifications: boolean;
  hasAvatar: boolean;
  assignedAdminsIds: number[];
  placements: CandidatePlacement[];
  source: SourcesType;
  highlight: {
    name?: string[];
    emails?: string[];
    phones?: string[];
    coverLetter?: string[];
    cvText?: string[];
    'tags.name'?: string[];
    'sources.name'?: string[];
    'placements.job.title'?: string[];
    'placements.talentPool.title'?: string[];
    'placements.stage.name'?: string[];
  } | null;
  isAnonymous?: boolean;
};
export type FullSearchCandidatesResponse = {
  total: number;
  hits: FullSearchCandidateRecord[];
  aggregations: FullSearchAggregationsV2;
};

export type FullSearchNoteRecord = {
  id: number;
  companyId: number;
  createdAt: string;
  updatedAt: string;
  body: string;
  bodyHtml: string;
  bodyText: string;
  private: boolean;
  adminId: number | null;
  adminName: string | null;
  guestName: string | null;
  assignedAdminsIds: number[];
  candidate: FullSearchCandidateRecord;
  highlight: {
    bodyHtml?: string[];
    bodyText?: string[];
    'candidate.name'?: string[];
  };
  visibility?: Visibility;
};
export type FullSearchNotesResponse = {
  total: number;
  hits: FullSearchNoteRecord[];
  aggregations: FullSearchAggregationsV2;
};

export type CandidatePlacement = {
  id: number;
  offer: {
    id: number;
    title: string;
    kind: TOfferKind;
    status: TOfferStatus;
    locationIds: number[];
  } | null;
  stage: {
    id: number;
    name: string;
  } | null;
  disqualified: boolean;
  disqualifiedAt: string;
  disqualifyReason: string;
  disqualifyKind: DisqualifyKind;
  disqualifiedBy?: number | null;
  disqualifiedByName?: string | null;
  overdueAt: string | null;
  overdueDiff: number | null;
  eeoDataStatus: EEODataStatus | null;
  positiveRatings: number | null;
  ratingVisible: boolean;
  hiredAt: string;
  jobStartsAt: string;
  isHired: boolean;
  locationIds: number[];
};
